import type { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form"
import { useEffect, useRef } from "react"

import { toast } from "@/components/toast"
import { captureError } from "@/lib/capture-error"

/**
 * Saves a form every time the user changes a field. A change that fails the
 * schema is never silent: the user sees a toast and Sentry gets the field names.
 */
export function useAutoSave<T extends FieldValues>({
	form,
	onSave,
	action,
}: {
	form: UseFormReturn<T>
	onSave: (values: T) => void | Promise<void>
	action: string
}) {
	const latest = useRef(onSave)
	latest.current = onSave

	useEffect(() => {
		const onInvalid = (errors: FieldErrors<T>) => {
			toast.error({
				title: "Your change didn't save",
				description: "Reload the page and try again.",
			})
			captureError(new Error("Autosave form failed validation"), {
				domain: "general",
				action,
				extra: { fields: Object.keys(errors) },
			})
		}
		const subscription = form.watch((_, { type }) => {
			if (type !== "change") return
			void form.handleSubmit((values) => latest.current(values), onInvalid)()
		})
		return () => subscription.unsubscribe()
	}, [form, action])
}
